/**
 * ProvidersSection - Displays all training providers with their artifact counts
 * 
 * Shows a list of all active training providers from the training catalog
 * with the number of artifacts associated with each provider.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Building2 } from "lucide-react";
import { useAllTrainingProviders } from "@/hooks/useCreatorAdminAnalytics";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProviderWithCounts {
  id: string;
  name: string;
  display_name: string;
  artifact_count: number;
  creator_count: number;
}

export function ProvidersSection() {
  const { data: providers, isLoading: providersLoading } = useAllTrainingProviders();
  
  // Fetch artifact counts per provider
  const { data: providerCounts } = useQuery({
    queryKey: ['provider-counts'],
    queryFn: async () => {
      // Get artifact counts by provider
      const { data: artifacts, error: artifactError } = await supabase
        .from('training_artifacts')
        .select('provider_id')
        .eq('is_active', true);
      
      if (artifactError) throw artifactError;
      
      // Get creator counts by provider
      const { data: creators, error: creatorError } = await supabase
        .from('training_creator_accounts')
        .select('provider_id')
        .not('provider_id', 'is', null);
      
      if (creatorError) throw creatorError;
      
      // Build count maps
      const artifactCounts = new Map<string, number>();
      const creatorCounts = new Map<string, number>();
      
      (artifacts || []).forEach(a => {
        if (a.provider_id) {
          artifactCounts.set(a.provider_id, (artifactCounts.get(a.provider_id) || 0) + 1);
        }
      });
      
      (creators || []).forEach(c => {
        if (c.provider_id) {
          creatorCounts.set(c.provider_id, (creatorCounts.get(c.provider_id) || 0) + 1);
        }
      });
      
      return { artifactCounts, creatorCounts };
    },
  });

  if (providersLoading) {
    return (
      <Card>
        <CardContent className="py-4">
          <div className="text-sm text-muted-foreground">Loading providers...</div>
        </CardContent>
      </Card>
    );
  }

  const providersWithCounts: ProviderWithCounts[] = (providers || []).map(p => ({
    ...p,
    artifact_count: providerCounts?.artifactCounts.get(p.id) || 0,
    creator_count: providerCounts?.creatorCounts.get(p.id) || 0,
  }));

  // Sort by artifact count descending
  providersWithCounts.sort((a, b) => b.artifact_count - a.artifact_count);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Training Catalog Providers
        </CardTitle>
        <CardDescription>
          Providers from the training catalog. Link creators to providers to auto-assign their artifacts.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {providersWithCounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No providers in the training catalog yet.</p>
        ) : (
          <div className="space-y-2">
            {providersWithCounts.map((provider) => (
              <div
                key={provider.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium">{provider.display_name}</span>
                    <div className="text-xs text-muted-foreground">{provider.name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {provider.artifact_count} artifact{provider.artifact_count !== 1 ? 's' : ''}
                  </Badge>
                  {provider.creator_count > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {provider.creator_count} creator{provider.creator_count !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
